/// Monochrome token isotipo: a black (ink) disc with a white (paper) mark. AVAX/GGP paint their real
/// mark via a CSS mask so the fill follows the theme; BTC/USDC use their symbol; ETH its diamond.
/// One shared component so every surface (vault cards, deposit input, hero) reads identically.

const MASK_LOGO: Record<string, string> = {
  avax: '/assets/img/token/avax-mono.svg',
  ggp: '/assets/img/token/ggp.svg',
}
const GLYPH: Record<string, string> = { btc: '₿', usdc: '$' }

// Accepts either the short id ('avax') or a display name ('AVAX', 'GGP') — normalised here.
function normalise(token: string): string {
  const t = token.toLowerCase()
  if (t.includes('ggp')) return 'ggp'
  if (t.includes('avax')) return 'avax'
  if (t.includes('btc')) return 'btc'
  if (t.includes('eth')) return 'eth'
  if (t.includes('usdc')) return 'usdc'
  return t
}

export function CoinMark({
  size = 44,
  token,
  variant = 'ink',
}: {
  size?: number
  token: string
  variant?: 'ink' | 'light'
}) {
  const id = normalise(token)
  const mask = MASK_LOGO[id]
  const markSize = Math.round(size * 0.52)
  // 'ink' → black disc + white mark; 'light' → white disc + black mark (used on the coloured hero).
  const discBg = variant === 'light' ? '#ffffff' : 'var(--ink)'
  const markColor = variant === 'light' ? '#16181c' : 'var(--paper)'

  let inner: React.ReactNode
  if (mask) {
    inner = (
      <span
        aria-hidden="true"
        style={{
          background: markColor,
          height: markSize,
          maskImage: `url('${mask}')`,
          maskPosition: 'center',
          maskRepeat: 'no-repeat',
          maskSize: 'contain',
          WebkitMaskImage: `url('${mask}')`,
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          width: markSize,
        }}
      />
    )
  } else if (id === 'eth') {
    inner = (
      <svg aria-hidden="true" height={markSize} viewBox="0 0 32 32" width={markSize}>
        <g fill="currentColor">
          <path d="M16 3.5 8.75 16.02 16 13.02z" fillOpacity="0.85" />
          <path d="M16 3.5v9.52l7.25 3z" />
          <path d="M16 21.9 8.75 17.6 16 28z" fillOpacity="0.85" />
          <path d="M16 28 23.25 17.6 16 21.9z" />
          <path d="M8.75 16.02 16 20.32V13.02z" fillOpacity="0.5" />
          <path d="M16 13.02v7.3l7.25-4.3z" fillOpacity="0.7" />
        </g>
      </svg>
    )
  } else {
    inner = GLYPH[id] ?? '◎'
  }

  return (
    <span
      aria-hidden="true"
      className="grid flex-none place-items-center rounded-full"
      style={{
        background: discBg,
        color: markColor,
        fontSize: Math.round(size * 0.46),
        fontWeight: 500,
        height: size,
        width: size,
      }}
    >
      {inner}
    </span>
  )
}
